/* tslint:disable */
import { injectable } from 'inversify';
import { IEnvironmentService, ParsedArgs, IExtensionHostDebugParams, IDebugParams } from '../interfaces';
import * as os from 'os';
import * as path from 'path';
import { memoize } from '@vscode/base-node/vs/base/common/decorators';
import product from '../product';
import { parseUserDataDir, parsePathArg, parseExtensionHostPort, getIPCHandle, parseSearchPort } from '../utils';
import { URI } from '@vscode/base-node/vs/base/common/uri';
import { getPathFromAmdModule } from '@vscode/base-node/vs/base/common/amd';
import { isWindows, isLinux } from '@vscode/base-node/vs/base/common/platform';
import { toLocalISOString } from '@vscode/base-node/vs/base/common/date';
import * as LogLevel from 'bunyan';

function getCLIPath(execPath: string, appRoot: string, isBuilt: boolean): string {

  // Windows
  if (isWindows) {
    if (isBuilt) {
      return path.join(path.dirname(execPath), 'bin', `${product.applicationName}.cmd`);
    }

    return path.join(appRoot, 'scripts', 'code-cli.bat');
  }

  // Linux
  if (isLinux) {
    if (isBuilt) {
      return path.join(path.dirname(execPath), 'bin', `${product.applicationName}`);
    }

    return path.join(appRoot, 'scripts', 'code-cli.sh');
  }

  // macOS
  if (isBuilt) {
    return path.join(appRoot, 'bin', 'code');
  }

  return path.join(appRoot, 'scripts', 'code-cli.sh');
}

@injectable()
export class EnvironmentService implements IEnvironmentService {

  get args(): ParsedArgs { return this._args; }

  @memoize
  get appRoot(): string { return path.dirname(getPathFromAmdModule(require, '')); }

  get execPath(): string { return this._execPath; }

  readonly logsPath: string;

  @memoize
  get cliPath(): string { return getCLIPath(this.execPath, this.appRoot, this.isBuilt); }

  @memoize
  get userHome(): string { return os.homedir(); }

  @memoize
  get userDataPath(): string {
    if (process.env['VSCODE_PORTABLE']) {
      return path.join(process.env['VSCODE_PORTABLE'], 'user-data');
    }

    return parseUserDataDir(this._args, process);
  }

  get appNameLong(): string { return product.nameLong; }

  get appQuality(): string { return product.quality; }

  @memoize
  get appSettingsHome(): string { return path.join(this.userDataPath, 'User'); }

  @memoize
  get appSettingsPath(): string { return path.join(this.appSettingsHome, 'settings.json'); }

	 @memoize
	get settingsSearchBuildId(): number { return product.settingsSearchBuildId; }

	 @memoize
	get settingsSearchUrl(): string { return product.settingsSearchUrl; }

  @memoize
  get workspaceStorageHome(): string { return path.join(this.appSettingsHome, 'workspaceStorage'); }

  @memoize
  get appKeybindingsPath(): string { return path.join(this.appSettingsHome, 'keybindings.json'); }

  @memoize
  get isExtensionDevelopment(): boolean { return !!this._args.extensionDevelopmentPath; }

  @memoize
  get backupHome(): string { return path.join(this.userDataPath, 'Backups'); }

  @memoize
  get backupWorkspacesPath(): string { return path.join(this.backupHome, 'workspaces.json'); }

  @memoize
  get workspacesHome(): string { return path.join(this.userDataPath, 'Workspaces'); }

  @memoize
  get installSourcePath(): string { return path.join(this.userDataPath, 'installSource'); }

  @memoize
  get builtinExtensionsPath(): string {
    const fromArgs = parsePathArg(this._args['builtin-extensions-dir'], process);
    if (fromArgs) {
      return fromArgs;
    } else {
      return path.normalize(path.join(getPathFromAmdModule(require, ''), '..', 'extensions'));
    }
  }

  @memoize
  get extensionsPath(): string {
    const fromArgs = parsePathArg(this._args['extensions-dir'], process);

    if (fromArgs) {
      return fromArgs;
    } else if (process.env['VSCODE_EXTENSIONS']) {
      return process.env['VSCODE_EXTENSIONS'];
    } else if (process.env['VSCODE_PORTABLE']) {
      return path.join(process.env['VSCODE_PORTABLE'], 'extensions');
    } else {
      return path.join(this.userHome, product.dataFolderName, 'extensions');
    }
  }

  @memoize
  get extensionDevelopmentLocationURI(): URI {
    const s = this._args.extensionDevelopmentPath;
    if (s) {
      if (/^[^:/?#]+?:\/\//.test(s)) {
        return URI.parse(s);
      }
      return URI.file(path.normalize(s));
    }
    return void 0;
  }

  @memoize
  get extensionTestsPath(): string { return this._args.extensionTestsPath ? path.normalize(this._args.extensionTestsPath) : this._args.extensionTestsPath; }

	get disableExtensions(): boolean | string[] {
		if (this._args['disable-extensions']) {
			return true;
		}
		const disableExtensions: string | string[] = this._args['disable-extension'];
		if (disableExtensions) {
			if (typeof disableExtensions === 'string') {
				return [disableExtensions];
			}
			if (Array.isArray(disableExtensions) && disableExtensions.length > 0) {
				return disableExtensions;
			}
		}
		return false;
	}

  get skipGettingStarted(): boolean { return this._args['skip-getting-started']; }

  get skipReleaseNotes(): boolean { return this._args['skip-release-notes']; }

  get skipAddToRecentlyOpened(): boolean { return this._args['skip-add-to-recently-opened']; }

  @memoize
  get debugExtensionHost(): IExtensionHostDebugParams { return parseExtensionHostPort(this._args, this.isBuilt); }

  @memoize
  get debugSearch(): IDebugParams { return parseSearchPort(this._args, this.isBuilt); }

  get isBuilt(): boolean { return !process.env['VSCODE_DEV']; }
  get verbose(): boolean { return this._args.verbose; }

  @memoize
  get logLevel(): any {
    if (this.verbose) {
      return LogLevel.TRACE;
    }
    if (typeof this._args.log === 'string') {
      const logLevel = this._args.log.toLowerCase();
      switch (logLevel) {
        case 'trace':
          return LogLevel.TRACE;
        case 'debug':
          return LogLevel.DEBUG;
        case 'info':
          return LogLevel.INFO;
        case 'warn':
          return LogLevel.WARN;
        case 'error':
          return LogLevel.ERROR;
        case 'critical':
          return LogLevel.FATAL;
        case 'off':
          return LogLevel.INFO;
      }
    }
    return LogLevel.INFO;
  }
  get log(): string { return this._args.log; }

  get wait(): boolean { return this._args.wait; }
  get logExtensionHostCommunication(): boolean { return this._args.logExtensionHostCommunication; }

  get performance(): boolean { return this._args.performance; }
  get status(): boolean { return this._args.status; }

  @memoize
  get mainIPCHandle(): string { return getIPCHandle(this.userDataPath, 'main'); }

  @memoize
  get sharedIPCHandle(): string { return getIPCHandle(this.userDataPath, 'shared'); }

  @memoize
  get nodeCachedDataDir(): string { return process.env['VSCODE_NODE_CACHED_DATA_DIR'] || undefined; }

  get disableUpdates(): boolean { return !!this._args['disable-updates']; }
  get disableCrashReporter(): boolean { return !!this._args['disable-crash-reporter']; }

  get driverHandle(): string { return this._args['driver']; }
  get driverVerbose(): boolean { return this._args['driver-verbose']; }

  constructor(private _args: ParsedArgs, private _execPath: string) {
    if (!process.env['VSCODE_LOGS']) {
      const key = toLocalISOString(new Date()).replace(/-|:|\.\d+Z$/g, '');
      process.env['VSCODE_LOGS'] = path.join(this.userDataPath, 'logs', key);
    }

    this.logsPath = process.env['VSCODE_LOGS'];
  }
}


